import { Button } from '@ophelos/ui/components/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ophelos/ui/components/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { COOKIE_CONSENT_COPY, type CookieBannerUiCopy } from '@ophelos/ui/components/legal/cookie-consent-copy';
import { Switch } from '@ophelos/ui/components/switch';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

export interface CookieCategory {
  key: string;
  label: string;
  /** Strictly-necessary category: always granted, shown non-toggleable. */
  essential: boolean;
  /** Part of the config contract; the schema requires it to be false for
   * non-essential categories. The banner never seeds a non-essential toggle from
   * it — optional consent always needs an affirmative click, so a misconfigured
   * `true` can't pre-tick a box. */
  default_granted: boolean;
  /** Expandable detail shown under the toggle in the preferences panel. */
  description?: string;
}

export type ConsentByCategory = Record<string, boolean>;

export interface CookieConsentBannerProps {
  open: boolean;
  categories: CookieCategory[];
  /** Link to the cookie policy shown in the notice. */
  policyUrl?: string;
  /** Intro paragraph shown on the summary bar and the preferences panel. */
  intro?: string;
  /** Static banner chrome (headings, buttons, labels); language-resolved by the caller. */
  ui?: CookieBannerUiCopy;
  /** Consequence-of-declining note shown at the foot of the preferences panel. */
  declineNote?: string;
  /** 'initial' opens on the summary bar with Accept/Reject; 'manage' opens
   * straight into the preferences panel (persistent "Cookie settings" entry). */
  mode?: 'initial' | 'manage';
  /** Current stored choice, used to pre-fill the preferences toggles. */
  initialConsent?: ConsentByCategory;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: (consent: ConsentByCategory) => void;
  /** Called when the banner should be dismissed (e.g. closing the manage panel
   * that was opened from the persistent settings link). */
  onOpenChange?: (open: boolean) => void;
}

function initialToggles(categories: CookieCategory[], stored: ConsentByCategory | undefined): ConsentByCategory {
  const next: ConsentByCategory = {};
  for (const c of categories) {
    // Essential is always granted; a non-essential toggle starts from the stored
    // choice and otherwise off. We never seed a non-essential from config, so a
    // misconfigured `default_granted: true` can't produce a pre-ticked box —
    // granting optional consent always requires an affirmative click.
    next[c.key] = c.essential ? true : (stored?.[c.key] ?? false);
  }
  return next;
}

// Fallbacks for callers that omit `intro`/`ui`; the real callers pass a
// language-aware copy from COOKIE_CONSENT_COPY. Sourced from the copy file so
// they can't drift.
const DESCRIPTION = COOKIE_CONSENT_COPY.en.intro;
const DEFAULT_UI = COOKIE_CONSENT_COPY.en.ui;

/**
 * GDPR cookie-consent banner. Presentational only — persistence and logging are
 * the caller's job.
 *
 * The initial notice is a **non-blocking** bottom bar: no overlay and no focus
 * trap, so the rest of the site (including login) stays usable and ignoring it
 * simply leaves the bar in place (no cookie wall). "Manage preferences" opens a
 * modal with per-category toggles — a deliberate, user-invoked interaction.
 * Equal-prominence Accept-all / Reject-all; no pre-ticked non-essential boxes.
 */
export function CookieConsentBanner({
  open,
  categories,
  policyUrl,
  intro = DESCRIPTION,
  ui = DEFAULT_UI,
  declineNote,
  mode = 'initial',
  initialConsent,
  onAcceptAll,
  onRejectAll,
  onSave,
  onOpenChange,
}: CookieConsentBannerProps) {
  const [view, setView] = React.useState<'summary' | 'preferences'>(mode === 'manage' ? 'preferences' : 'summary');
  const [toggles, setToggles] = React.useState<ConsentByCategory>(() => initialToggles(categories, initialConsent));

  // Reset to the requested view and re-seed toggles whenever the banner is opened.
  React.useEffect(() => {
    if (open) {
      setView(mode === 'manage' ? 'preferences' : 'summary');
      setToggles(initialToggles(categories, initialConsent));
    }
  }, [open, mode, categories, initialConsent]);

  if (!open) return null;

  const policyLink = policyUrl && (
    <Button asChild variant="link" size="link">
      <a href={policyUrl} target="_blank" rel="noopener noreferrer">
        {ui.readPolicy}
      </a>
    </Button>
  );

  if (view === 'preferences') {
    // Closing the panel returns to the bar when it was reached via "Manage
    // preferences"; when opened directly from the settings link it dismisses.
    const handleOpenChange = (next: boolean) => {
      if (next) return;
      if (mode === 'manage') onOpenChange?.(false);
      else setView('summary');
    };

    return (
      <Dialog open onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{ui.preferencesTitle}</DialogTitle>
            <DialogDescription>
              {intro} {policyLink}
            </DialogDescription>
          </DialogHeader>

          <ul className="stack gap-4 py-1">
            {categories.map((category) => (
              <li key={category.key} className="stack gap-2 border-b border-border-light pb-4 last:border-b-0 last:pb-0">
                <div className="flex items-center justify-between gap-4">
                  <label htmlFor={`consent-${category.key}`} className="text-sm text-foreground">
                    {category.label}
                    {category.essential && <span className="ml-2 text-xs text-muted-foreground">{ui.alwaysOn}</span>}
                  </label>
                  <Switch
                    id={`consent-${category.key}`}
                    checked={category.essential ? true : (toggles[category.key] ?? false)}
                    disabled={category.essential}
                    onCheckedChange={(checked) => setToggles((prev) => ({ ...prev, [category.key]: checked }))}
                  />
                </div>
                {category.description && (
                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <Button variant="linkMuted" size="link" className="group gap-1">
                        {ui.details}
                        <ChevronDown className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-1 text-sm text-muted-foreground">{category.description}</CollapsibleContent>
                  </Collapsible>
                )}
              </li>
            ))}
          </ul>

          {declineNote && <p className="text-xs text-muted-foreground">{declineNote}</p>}

          <DialogFooter>
            {mode === 'initial' && (
              <Button variant="ghost" onClick={() => setView('summary')}>
                {ui.back}
              </Button>
            )}
            <Button variant="default" onClick={() => onSave(toggles)}>
              {ui.savePreferences}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <section
      aria-label={ui.bannerAriaLabel}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card text-card-foreground shadow-lg"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
        <div className="stack gap-1">
          <p className="text-sm font-semibold">{ui.summaryHeading}</p>
          <p className="text-sm text-muted-foreground">
            {intro} {policyLink}
          </p>
          <div>
            <Button variant="link" size="link" onClick={() => setView('preferences')}>
              {ui.managePreferences}
            </Button>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row md:flex-col lg:flex-row">
          <Button variant="default" onClick={onRejectAll}>
            {ui.rejectAll}
          </Button>
          <Button variant="default" onClick={onAcceptAll}>
            {ui.acceptAll}
          </Button>
        </div>
      </div>
    </section>
  );
}
